import React, { ReactNode, useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalProps = {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  describedBy?: string;
  size?: 'md' | 'lg' | 'xl';
  tone?: 'light' | 'dark';
  children: ReactNode;
};
const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
const sizes = {
  md: 'max-w-[560px]',
  lg: 'max-w-[880px]',
  xl: 'max-w-[1180px]'
};
export function Modal({
  open,
  onClose,
  labelledBy,
  describedBy,
  size = 'md',
  tone = 'light',
  children
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus({
        preventScroll: true
      });
    }, 20);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus({
        preventScroll: true
      });
    };
  }, [open, onClose]);
  const panelTone = tone === 'dark' ? 'bg-charcoal text-ivory' : 'bg-ivory text-charcoal';
  return <AnimatePresence data-id="element-182" data-mp="1hdjowkr3umez">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: reduce ? 0 : 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-183" data-mp="1hci43okaelk2">
          <div className="absolute inset-0 bg-charcoal/80" onClick={onClose} aria-hidden="true" data-id="element-184" data-mp="1cezzubh7suie" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-describedby={describedBy} className={`relative max-h-[calc(100vh-2rem)] w-full overflow-y-auto ${sizes[size]} ${panelTone}`} initial={reduce ? false : {
        opacity: 0,
        scale: 0.97,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.98,
        y: 4
      }} transition={{
        duration: reduce ? 0 : 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-185" data-mp="13ch0i1cn100w">
            <button type="button" onClick={onClose} aria-label="Close" className={`focus-ring absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center transition-colors duration-150 ${tone === 'dark' ? 'bg-charcoal/70 text-ivory hover:bg-charcoal' : 'bg-ivory/90 text-charcoal hover:bg-stone'}`} data-id="element-186" data-mp="r27ul3yxcl9v">
              <XIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-187" data-mp="19r63x92t0pp8" />
            </button>
            {children}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}